import React from 'react';
import { observer } from 'mobx-react-lite';
import { Popup } from 'src/domains/layouts/shared/popup/Popup';
import { SuccessPopup } from 'src/domains/layouts/shared/popup/popups/cms/SuccessPopup';
import { FormComponent, FormWrapper } from 'src/domains/layouts/shared/cms/form/Form.style';
import { FormHeader } from 'src/domains/layouts/shared/cms/form/formHeader/FormHeader';
import { FormButtons } from 'src/domains/layouts/shared/buttons/FormButtons';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { Select } from 'src/domains/layouts/shared/formComponents/Select/components/new/Select';
import { SeoFormState } from 'src/main/cms/SeoManagement/SeoForm/SeoFormState';
import { SeoPageTranslationsForm } from 'src/main/cms/SeoManagement/SeoForm/SeoPageTranslationsForm';
import { AddTranslationButton } from 'src/domains/layouts/shared/cms/translationsForm/CmsTranslationsForm.style';
import { ErrorMessageTop } from 'src/domains/layouts/shared/cms/list/List.style';

interface SeoPageFormPropsType {
    seoFormState: SeoFormState;
    dataTest: string;
}

export const SeoPageForm = observer(({ seoFormState, dataTest }: SeoPageFormPropsType) => {
    const {
        handleCreate,
        handleEdit,
        resetForm,
        isEditMode,
        isLoading,
        selectedSeoPage,
        selectedPage,
        pageSlug,
        title,
        fullDescription,
        shortDescription,
        listOptions,
        sportListOptions,
        casinoListOptions,
        handlePageSelect,
        handleSubpageSelect,
        languagesState,
        showCreatedPopup,
        handleHideCreatedPopup,
        errorMessage,
    } = seoFormState;

    return (
        <FormWrapper
            isEditMode={isEditMode}
            data-test={dataTest}
        >
            <FormHeader
                isEditMode={isEditMode}
                defaultLabel='Create new'
                editModeLabel='Edit page'
                resetForm={resetForm}
            />
            <FormComponent
                onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                    e.preventDefault();
                }}
            >
                <Loader isActive={isLoading} />

                {isEditMode ? null : (
                    <>
                        <Select
                            placeholder='-Select destination-'
                            stateValue={selectedPage}
                            selectOptions={listOptions}
                            onChangeCB={handlePageSelect}
                            dataTest='select-seo-destination'
                        />
                        {selectedSeoPage === 'homepage' || selectedSeoPage === 'inplay' ? null : selectedSeoPage ===
                          'casino' ? (
                            <Select
                                placeholder='-Select page-'
                                stateValue={pageSlug}
                                selectOptions={casinoListOptions}
                                onChangeCB={handleSubpageSelect}
                                dataTest='select-casino-page'
                            />
                        ) : (
                            <Select
                                placeholder='-Select page-'
                                stateValue={pageSlug}
                                selectOptions={sportListOptions}
                                onChangeCB={handleSubpageSelect}
                                dataTest='select-sport-page'
                            />
                        )}
                    </>
                )}
                <Input
                    formInputState={title}
                    isEmpty={title.value === ''}
                    label='Title'
                    dataTest='seo-description-title'
                />
                <Textarea
                    formState={shortDescription}
                    isEmpty={shortDescription.value === ''}
                    label='Short description'
                    dataTest='seo-short-description'
                />
                <Textarea
                    formState={fullDescription}
                    ckeditor={true}
                    isEmpty={fullDescription.value === ''}
                    label='Long description'
                    dataTest='seo-long-description'
                />

                {seoFormState.formsIds.map((item) => {
                    const state = seoFormState.getTranslationFormStateById(item);
                    return (
                        <SeoPageTranslationsForm
                            key={item}
                            seoPageTranslationFormState={state}
                            seoFormState={seoFormState}
                            languagesState={languagesState}
                            formId={item}
                        />
                    );
                })}
                {seoFormState.formsIds.length === 0 && languagesState.getNonDefaultLanguagesList().length > 0 ? (
                    <AddTranslationButton
                        addAnotherForm={false}
                        version='secondary'
                        width='full'
                        onClick={seoFormState.addLanguageForm}
                    >
                        Add language
                    </AddTranslationButton>
                ) : null}

                <FormButtons
                    onCreate={handleCreate}
                    onUpdate={handleEdit}
                    onCancel={resetForm}
                    isEditMode={isEditMode}
                    dataTestCreate='create-button'
                />

                {showCreatedPopup === false ? null : (
                    <Popup
                        width={384}
                        handleHidePopup={handleHideCreatedPopup}
                    >
                        <SuccessPopup
                            handleHidePopup={handleHideCreatedPopup}
                            itemLabel='seo content'
                        />
                    </Popup>
                )}

                {errorMessage !== null && <ErrorMessageTop>{errorMessage}</ErrorMessageTop>}
            </FormComponent>
        </FormWrapper>
    );
});
